import {
  CancelScheduleSendOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  HistoryOutlined,
  ModeEditOutline,
  InfoOutlined,
  ReplayOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { StockBreakdownPopover } from '../../../../../../features/stock';
import VoyageRemarksModal from './VoyageRemarksModal';
import getVoyageStatusChip from './VoyageStatusUtils';
import { formatNumberDE } from '../../../../../../shared/utils/formatNumber';
import {
  scenarioDeltaTextSx,
  scenarioCreatedBgColor,
  getScenarioHighlightColor,
} from '../../../../../../shared/utils/scenarioHighlight';
import useScenarioMode from '../../../../../../shared/hooks/useScenarioMode';

// Utility Functions
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date)
    ? 'Invalid Date'
    : new Intl.DateTimeFormat('en-DE', {
        day: 'numeric',
        month: 'short',
      }).format(date);
};

const getDelayColor = (delay) => {
  if (delay < 4) return 'success.main';
  if (delay >= 4 && delay <= 6) return 'warning.main';
  return 'error.main';
};

const formatDelay = (delay) => {
  const sign = delay < 0 ? '-' : '+';
  return `${sign}${Math.abs(delay)}`;
};

const calculateSum = (shipments, key, isStock = false) => {
  return (shipments ?? []).reduce((sum, shipment) => {
    const value = isStock ? (shipment.stock?.[key] ?? 0) : (shipment[key] ?? 0);
    return sum + value;
  }, 0);
};

const getDeltaSx = (theme) => scenarioDeltaTextSx(theme);

// Styled Components
const CenteredGrid = styled(Grid)(({ theme }) => ({
  textAlign: 'center',
  justifyContent: 'center',
  alignItems: 'center',
}));

const BorderedGrid = styled(Grid)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const PaddedBox = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
}));

// Reusable Components
const PortInfo = ({ port, changed }) => {
  const theme = useTheme();
  const deltaSx = getDeltaSx(theme);
  return (
    <PaddedBox>
      <Typography variant='h3' sx={changed ? deltaSx : undefined}>
        {port?.unLocode ?? 'N/A'}
      </Typography>
      <Typography
        variant='body2'
        sx={changed ? deltaSx : { color: 'text.secondary' }}
      >
        {port?.name ?? 'Unknown'}
      </Typography>
    </PaddedBox>
  );
};

const DelayInfo = ({ label, date, initialDate, delay, changed }) => {
  const theme = useTheme();
  const deltaSx = getDeltaSx(theme);
  return (
    <CenteredGrid xs={3} sx={{ flexDirection: 'column' }}>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant='h3' sx={changed ? deltaSx : undefined}>
        {formatDate(date)}
      </Typography>
      <Tooltip title={`Initial ${label}: ${formatDate(initialDate)}`}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant='body1' sx={{ color: getDelayColor(delay) }}>
            {formatDelay(delay)}
          </Typography>
          <Typography variant='body1' sx={{ ml: 1 }}>
            Days
          </Typography>
        </Box>
      </Tooltip>
    </CenteredGrid>
  );
};

const ShipmentRow = ({ shipment, baseShipment }) => {
  const theme = useTheme();

  const changed = (field) => {
    if (!baseShipment) return false;
    return String(shipment[field] ?? '') !== String(baseShipment[field] ?? '');
  };
  const marketChanged = baseShipment
    ? (shipment.market?.id ?? '') !== (baseShipment.market?.id ?? '')
    : false;
  // If no baseShipment exists but we have a shipment, it's a new row — highlight all
  const isNew = baseShipment === undefined;

  return (
    <>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={marketChanged || isNew ? getDeltaSx(theme) : undefined}
        >
          {`${shipment.market?.country?.name ?? ''}`}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={
            changed('firstShippingLineOffer') || isNew
              ? getDeltaSx(theme)
              : undefined
          }
        >
          {shipment.firstShippingLineOffer != null
            ? formatNumberDE(shipment.firstShippingLineOffer)
            : 'N/A'}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={
            changed('adjustedPORequest') || isNew
              ? getDeltaSx(theme)
              : undefined
          }
        >
          {shipment.adjustedPORequest != null
            ? formatNumberDE(shipment.adjustedPORequest)
            : 'N/A'}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={
            changed('finalAgreement') || isNew ? getDeltaSx(theme) : undefined
          }
        >
          {shipment.finalAgreement != null
            ? formatNumberDE(shipment.finalAgreement)
            : 'N/A'}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={
            changed('effectiveShipped') || isNew ? getDeltaSx(theme) : undefined
          }
        >
          {shipment.effectiveShipped != null
            ? formatNumberDE(shipment.effectiveShipped)
            : 'N/A'}
        </Typography>
      </Grid>
      <Grid
        xs={2}
        paddingLeft={2}
        sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant='body1'>
          {shipment.stock?.readyForShipment != null
            ? formatNumberDE(shipment.stock.readyForShipment)
            : 'No Data'}
        </Typography>
      </Grid>
      <Grid xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='body1'>
          {shipment.stock?.totalBlockedCars != null
            ? formatNumberDE(shipment.stock.totalBlockedCars)
            : 'No Data'}
        </Typography>
        <StockBreakdownPopover stockBreakdown={shipment.stockBreakdown} />
      </Grid>
    </>
  );
};

// Main Component
const VoyageCard = ({
  data = {},
  baseVoyage = null,
  CardHeader,
  CardContainer,
  onEditClicked,
  onCancelVoyageClicked = () => undefined,
  onUnCancelVoyageClicked = () => undefined,
  loadingVoyageId = null,
  handleHistoryClick = () => undefined,
}) => {
  const { isScenarioViewOnly, isScenarioActive } = useScenarioMode();
  const [expanded, setExpanded] = useState(false);
  const [isVoyageRemarkModalOpen, setIsVoyageRemarkModalOpen] = useState(false);
  const theme = useTheme();

  // Check if ANY shipment has stock data for the summary row
  const hasAnyStock = useMemo(
    () => (data.shipments ?? []).some((s) => s.stock != null),
    [data.shipments]
  );

  const sums = useMemo(
    () => ({
      firstShippingLineOffer: calculateSum(
        data.shipments,
        'firstShippingLineOffer'
      ),
      adjustedPORequest: calculateSum(data.shipments, 'adjustedPORequest'),
      finalAgreement: calculateSum(data.shipments, 'finalAgreement'),
      effectiveShipped: calculateSum(data.shipments, 'effectiveShipped'),
      readyForShipment: calculateSum(data.shipments, 'readyForShipment', true),
      totalBlockedCars: calculateSum(data.shipments, 'totalBlockedCars', true),
    }),
    [data.shipments]
  );

  // ── Scenario highlighting: value-comparison change flags ──
  const baseShipmentMap = useMemo(() => {
    if (!baseVoyage?.shipments) return null;
    const map = {};
    for (const s of baseVoyage.shipments) {
      if (s.id) map[s.id] = s;
    }
    return map;
  }, [baseVoyage]);

  const baseSums = useMemo(() => {
    if (!baseVoyage) return null;
    return {
      firstShippingLineOffer: calculateSum(
        baseVoyage.shipments,
        'firstShippingLineOffer'
      ),
      adjustedPORequest: calculateSum(
        baseVoyage.shipments,
        'adjustedPORequest'
      ),
      finalAgreement: calculateSum(baseVoyage.shipments, 'finalAgreement'),
      effectiveShipped: calculateSum(baseVoyage.shipments, 'effectiveShipped'),
    };
  }, [baseVoyage]);

  const isScenarioCreatedVoyage = isScenarioActive && !baseVoyage;

  const deltaFlags = useMemo(() => {
    if (!isScenarioActive) return {};
    // Scenario-created voyage: highlight all fields
    if (!baseVoyage) {
      return {
        voyageNumber: true,
        shipName: true,
        shipImo: true,
        carrier: true,
        departurePort: true,
        destinationPort: true,
        actualETD: true,
        actualETA: true,
        sumFirstShippingLineOffer: true,
        sumAdjustedPORequest: true,
        sumFinalAgreement: true,
        sumEffectiveShipped: true,
      };
    }
    return {
      voyageNumber:
        String(data.voyageNumber ?? '') !==
        String(baseVoyage.voyageNumber ?? ''),
      shipName:
        String(data.ship?.name ?? '') !== String(baseVoyage.ship?.name ?? ''),
      shipImo:
        String(data.ship?.imo ?? '') !== String(baseVoyage.ship?.imo ?? ''),
      carrier:
        String(data.carrier?.supplierName ?? '') !==
        String(baseVoyage.carrier?.supplierName ?? ''),
      departurePort:
        String(data.departurePort?.unLocode ?? '') !==
        String(baseVoyage.departurePort?.unLocode ?? ''),
      destinationPort:
        String(data.destinationPort?.unLocode ?? '') !==
        String(baseVoyage.destinationPort?.unLocode ?? ''),
      actualETD:
        String(data.actualETD ?? '') !== String(baseVoyage.actualETD ?? ''),
      actualETA:
        String(data.actualETA ?? '') !== String(baseVoyage.actualETA ?? ''),
      sumFirstShippingLineOffer:
        baseSums &&
        sums.firstShippingLineOffer !== baseSums.firstShippingLineOffer,
      sumAdjustedPORequest:
        baseSums && sums.adjustedPORequest !== baseSums.adjustedPORequest,
      sumFinalAgreement:
        baseSums && sums.finalAgreement !== baseSums.finalAgreement,
      sumEffectiveShipped:
        baseSums && sums.effectiveShipped !== baseSums.effectiveShipped,
    };
  }, [isScenarioActive, baseVoyage, data, sums, baseSums]);

  const handleExpandClick = () => setExpanded(!expanded);

  const calculateProgress = (actualETD, actualETA) => {
    if (!actualETA) return null;
    const currentDate = new Date();
    const etdDate = new Date(actualETD);
    const etaDate = new Date(actualETA);

    if (isNaN(etdDate) || isNaN(etaDate) || currentDate < etdDate) return null;

    const totalDuration = etaDate - etdDate;
    const elapsedDuration = currentDate - etdDate;

    return Math.min((elapsedDuration / totalDuration) * 100, 100);
  };

  const progress = calculateProgress(data.actualETD, data.actualETA);

  const isHistoryButtonDisabled =
    !data.changed && !data.shipments?.some((s) => s.changed);
  const hasCurrentRemark = !!(
    data.generalRemarks && String(data.generalRemarks).trim().length > 0
  );

  const scenarioCreatedContainerSx = isScenarioCreatedVoyage
    ? {
        borderRadius: '6px',
        border: (t) => `2px solid ${getScenarioHighlightColor(t)}`,
        backgroundColor: (t) => scenarioCreatedBgColor(t),
      }
    : undefined;

  return (
    <Box sx={scenarioCreatedContainerSx}>
      <CardContainer>
        <Box sx={{ width: '100%' }}>
          {/* Header Section */}
          <Grid container spacing={2}>
            <Grid xs={10}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CardHeader
                  title={data.voyageNumber || 'N/A'}
                  sx={deltaFlags.voyageNumber ? getDeltaSx(theme) : undefined}
                />
                <PaddedBox>
                  <Typography
                    variant='h6'
                    sx={deltaFlags.shipName ? getDeltaSx(theme) : undefined}
                  >
                    {data.ship?.name ?? 'Unknown'}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={
                      deltaFlags.shipImo
                        ? getDeltaSx(theme)
                        : { color: 'text.secondary' }
                    }
                  >
                    {data.ship?.imo ?? 'N/A'}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={
                      deltaFlags.carrier
                        ? getDeltaSx(theme)
                        : { color: 'text.secondary' }
                    }
                  >
                    {data.carrier?.supplierName ?? 'N/A'}
                  </Typography>
                </PaddedBox>
              </Box>
            </Grid>
            <Grid
              xs={2}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {getVoyageStatusChip(
                data.actualETD,
                data.actualETA,
                data.cancelled,
                sums.effectiveShipped
              )}
            </Grid>
          </Grid>

          {/* Main Content Section */}
          <Grid
            container
            spacing={2}
            sx={{
              mt: 3,
              justifyContent: 'space-evenly',
              textAlign: 'center',
              alignItems: 'center',
            }}
          >
            <Grid xs={2}>
              <PortInfo
                port={data.departurePort}
                changed={deltaFlags.departurePort}
              />
            </Grid>
            <DelayInfo
              label='Departure'
              date={data.actualETD}
              initialDate={data.initialETD}
              delay={data.departureDelay}
              changed={deltaFlags.actualETD}
            />
            <CenteredGrid xs={1}>
              <Chip
                label={`${data.duration ?? 'N/A'} Days`}
                variant='outlined'
              />
            </CenteredGrid>
            <DelayInfo
              label='Arrival'
              date={data.actualETA}
              initialDate={data.initialETA}
              delay={data.arrivalDelay}
              changed={deltaFlags.actualETA}
            />
            <Grid xs={2}>
              <PortInfo
                port={data.destinationPort}
                changed={deltaFlags.destinationPort}
              />
            </Grid>
            {progress !== null && (
              <Grid xs={12}>
                <LinearProgress variant='determinate' value={progress} />
              </Grid>
            )}
          </Grid>

          {/* Shipment Summary Section */}
          <BorderedGrid container spacing={0.5} columns={19}>
            <Grid xs={3}>
              <Tooltip title={hasCurrentRemark ? 'View Remarks' : 'No Remarks'}>
                <span>
                  <IconButton
                    color={hasCurrentRemark ? 'primary' : 'default'}
                    disabled={!hasCurrentRemark}
                    onClick={() => setIsVoyageRemarkModalOpen(true)}
                  >
                    <InfoOutlined />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='View History'>
                <span>
                  <IconButton
                    color={!isHistoryButtonDisabled ? 'primary' : 'default'}
                    disabled={isHistoryButtonDisabled}
                    onClick={() => handleHistoryClick(data)}
                  >
                    <HistoryOutlined />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isScenarioViewOnly ? 'View' : 'Edit'}>
                <IconButton onClick={() => onEditClicked(data)}>
                  {isScenarioViewOnly ? (
                    <VisibilityOutlined />
                  ) : (
                    <ModeEditOutline />
                  )}
                </IconButton>
              </Tooltip>
              {!isScenarioViewOnly &&
                (loadingVoyageId === data.id ? (
                  <IconButton disabled>
                    <CircularProgress size={20} />
                  </IconButton>
                ) : data.cancelled ? (
                  <Tooltip title='Restore Voyage'>
                    <IconButton
                      color='primary'
                      onClick={() => onUnCancelVoyageClicked(data)}
                    >
                      <ReplayOutlined />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title='Cancel Voyage'>
                    <span>
                      <IconButton
                        color='error'
                        onClick={() => onCancelVoyageClicked(data)}
                      >
                        <CancelScheduleSendOutlined />
                      </IconButton>
                    </span>
                  </Tooltip>
                ))}
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle1'>
                First Shipping Line Offer
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle1'>Adjusted PO Request</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle1'>Final Agreement</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle1'>Effective Shipped</Typography>
            </Grid>
            <Grid
              xs={2}
              paddingLeft={2}
              sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant='subtitle1'>Ready For Shipment</Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant='subtitle1'>Blocked</Typography>
            </Grid>
            <Grid xs={3}>
              <IconButton
                onClick={handleExpandClick}
                aria-label={
                  expanded ? 'Collapse shipments' : 'Expand shipments'
                }
              >
                {expanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
              </IconButton>
            </Grid>
            <Grid xs={3}>
              <Typography
                variant='subtitle1'
                sx={
                  deltaFlags.sumFirstShippingLineOffer
                    ? getDeltaSx(theme)
                    : undefined
                }
              >
                {formatNumberDE(sums.firstShippingLineOffer)}
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography
                variant='subtitle1'
                sx={
                  deltaFlags.sumAdjustedPORequest
                    ? getDeltaSx(theme)
                    : undefined
                }
              >
                {formatNumberDE(sums.adjustedPORequest)}
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography
                variant='subtitle1'
                sx={
                  deltaFlags.sumFinalAgreement ? getDeltaSx(theme) : undefined
                }
              >
                {formatNumberDE(sums.finalAgreement)}
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography
                variant='subtitle1'
                sx={
                  deltaFlags.sumEffectiveShipped ? getDeltaSx(theme) : undefined
                }
              >
                {formatNumberDE(sums.effectiveShipped)}
              </Typography>
            </Grid>
            <Grid
              xs={2}
              paddingLeft={2}
              sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant='subtitle1'>
                {hasAnyStock
                  ? formatNumberDE(sums.readyForShipment)
                  : 'No Data'}
              </Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant='subtitle1'>
                {hasAnyStock
                  ? formatNumberDE(sums.totalBlockedCars)
                  : 'No Data'}
              </Typography>
            </Grid>
            {expanded &&
              (data.shipments ?? []).map((shipment) => (
                <ShipmentRow
                  key={shipment.id}
                  shipment={shipment}
                  baseShipment={
                    baseShipmentMap ? baseShipmentMap[shipment.id] : null
                  }
                />
              ))}
          </BorderedGrid>
        </Box>
        <VoyageRemarksModal
          isModalOpen={isVoyageRemarkModalOpen}
          onClose={() => setIsVoyageRemarkModalOpen(false)}
          currentVoyageData={data}
        />
      </CardContainer>
    </Box>
  );
};

// PropTypes for type safety
VoyageCard.propTypes = {
  baseVoyage: PropTypes.object,
  data: PropTypes.shape({
    id: PropTypes.string,
    voyageNumber: PropTypes.string,
    ship: PropTypes.shape({
      name: PropTypes.string,
      imo: PropTypes.string,
    }),
    carrier: PropTypes.shape({
      supplierName: PropTypes.string,
    }),
    departurePort: PropTypes.shape({
      unLocode: PropTypes.string,
      name: PropTypes.string,
    }),
    destinationPort: PropTypes.shape({
      unLocode: PropTypes.string,
      name: PropTypes.string,
    }),
    actualETD: PropTypes.string,
    initialETD: PropTypes.string,
    actualETA: PropTypes.string,
    initialETA: PropTypes.string,
    departureDelay: PropTypes.number,
    arrivalDelay: PropTypes.number,
    duration: PropTypes.number,
    cancelled: PropTypes.bool,
    generalRemarks: PropTypes.string,
    shipments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        market: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
        firstShippingLineOffer: PropTypes.number,
        adjustedPORequest: PropTypes.number,
        finalAgreement: PropTypes.number,
        effectiveShipped: PropTypes.number,
        stock: PropTypes.shape({
          readyForShipment: PropTypes.number,
          totalBlockedCars: PropTypes.number,
        }),
      })
    ),
  }).isRequired,
  CardHeader: PropTypes.elementType.isRequired,
  CardContainer: PropTypes.elementType.isRequired,
};

export default VoyageCard;
